import { useTranslation } from 'next-i18next';
import { useEffect, useState } from 'react';

import { IPage, ISEOOptions } from '@/types';

const pageSeoDescriptions: Record<string, string[]> = {
  'photo-and-video-recording': [
    'photo-video-recording.free-recording-description',
    'photo-video-recording.paid-recording-description',
  ],
  'rules-of-conduct': ['rules.hero'],
  'interactive-map': ['plan-visit.footer-text1', 'plan-visit.footer-text2', 'plan-visit.footer-text3'],
  'plan-your-visit': ['plan-visit.footer-text1', 'plan-visit.footer-text2', 'plan-visit.footer-text3'],
};

const parsePageDescription = async (html: string | null): Promise<string> => {
  try {
    const { convert } = await import('html-to-text');
    return convert(html ?? '', {
      wordwrap: false,
      limits: { maxChildNodes: 1 },
    });
  } catch (error) {
    return '';
  }
};

interface IUsePageSeo {
  seoOptions: ISEOOptions;
}

export const usePageSeo = (pageDetails: IPage | undefined): IUsePageSeo => {
  const { t } = useTranslation();
  const { name = '', page_body = null, gallery = [], slug = '', banner } = pageDetails ?? {};
  const [description, setDescription] = useState<string>('');

  useEffect(() => {
    const calculateDescription = async () => {
      const definedDescription: string[] | undefined = slug?.startsWith('department')
        ? [`department.${slug}.direction-text`]
        : pageSeoDescriptions[slug];

      const description: string = definedDescription?.length
        ? definedDescription.reduce((acc, chunk) => `${acc} ${t(chunk)}`.trim(), '')
        : await parsePageDescription(page_body);

      setDescription(description);
    };

    calculateDescription();
  }, [page_body, slug, t]);

  return {
    seoOptions: {
      title: name,
      description,
      images: gallery.map(({ img: url, title: alt = '' }) => ({ url, alt })),
      thumbnail: gallery?.find((image) => image.is_main)?.img || banner,
    },
  };
};
